import { Injectable } from '@angular/core';
import { CellError, HyperFormula } from 'hyperformula';
import { CalculationResult, Worksheet } from './spreadsheet.models';

@Injectable({ providedIn: 'root' })
export class SpreadsheetService {
  buildSheetMatrix(worksheet: Worksheet): Array<Array<string | number | null>> {
    const matrix: Array<Array<string | number | null>> = [];
    for (let row = 0; row < worksheet.rows; row++) {
      const rowValues: Array<string | number | null> = [];
      for (let column = 0; column < worksheet.columns; column++) {
        const cell = worksheet.cells[this.indexesToCellAddress(row, column)];
        rowValues.push(cell?.value ?? null);
      }
      matrix.push(rowValues);
    }
    return matrix;
  }

  recalculate(worksheet: Worksheet): CalculationResult {
    const values: Record<string, unknown> = {};
    const errors: Record<string, string> = {};
    let engine: HyperFormula | undefined;

    try {
      engine = HyperFormula.buildFromArray(this.buildSheetMatrix(worksheet), {
        licenseKey: 'gpl-v3',
      });
      const sheetValues = engine.getSheetValues(0);

      for (let row = 0; row < worksheet.rows; row++) {
        for (let column = 0; column < worksheet.columns; column++) {
          const address = this.indexesToCellAddress(row, column);
          const result = sheetValues[row]?.[column];
          values[address] = result ?? null;
          if (result instanceof CellError) {
            const cellError = result as unknown as CellError;
            errors[address] = cellError.message ?? String(cellError);
          }
        }
      }
    } catch (error) {
      // A malformed formula can prevent HyperFormula from building a sheet. Keep
      // the worksheet usable and mark formula cells with a user-facing error.
      for (const [address, cell] of Object.entries(worksheet.cells)) {
        if (cell.type === 'formula') {
          errors[address] = this.errorMessage(error);
          values[address] = null;
        } else {
          values[address] = cell.value ?? null;
        }
      }
    } finally {
      engine?.destroy();
    }

    return { values, errors };
  }

  cellAddressToIndexes(address: string): { row: number; column: number } | null {
    const match = /^([A-Z]+)([1-9][0-9]*)$/i.exec(address.trim());
    if (!match) {
      return null;
    }

    let column = 0;
    for (const character of match[1].toUpperCase()) {
      column = column * 26 + character.charCodeAt(0) - 64;
    }

    return { row: Number(match[2]) - 1, column: column - 1 };
  }

  indexesToCellAddress(row: number, column: number): string {
    let columnName = '';
    let remaining = column + 1;
    while (remaining > 0) {
      const remainder = (remaining - 1) % 26;
      columnName = String.fromCharCode(65 + remainder) + columnName;
      remaining = Math.floor((remaining - 1) / 26);
    }
    return `${columnName}${row + 1}`;
  }

  private errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'Invalid formula';
  }
}
